import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Building2, Fuel, Cpu, Wallet, ArrowRight, Users, TrendingUp, MapPin, Zap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export const Home = () => {
  const { language } = useLanguage();
  const t = translations.home;

  const subsidiaries = [
    {
      name: 'KIP ENERGY',
      tagline: t.subsidiaries.items[0].tagline[language],
      description: t.subsidiaries.items[0].description[language],
      icon: Fuel,
      color: '#FF6600',
      logo: 'https://customer-assets.emergentagent.com/job_ekip-showcase/artifacts/590yfomx_Logo%20KIP%20ENERGY.png'
    },
    {
      name: 'KIPYZ DESIGN & HOUSES',
      tagline: t.subsidiaries.items[1].tagline[language],
      description: t.subsidiaries.items[1].description[language],
      icon: Building2,
      color: '#00A651',
      logo: 'https://customer-assets.emergentagent.com/job_a6c32c3a-8d7a-49cd-8ea0-9f0781a3c364/artifacts/2re5u10q_Logo%20Filiale%20KIDH.png'
    },
    {
      name: 'EKIPTECH',
      tagline: t.subsidiaries.items[2].tagline[language],
      description: t.subsidiaries.items[2].description[language],
      icon: Cpu,
      color: '#003366',
      logo: 'https://customer-assets.emergentagent.com/job_a6c32c3a-8d7a-49cd-8ea0-9f0781a3c364/artifacts/3p5tqo9a_Logo%20Filiale%20Ekiptech.png'
    },
    {
      name: 'KIPAY',
      tagline: t.subsidiaries.items[3].tagline[language],
      description: t.subsidiaries.items[3].description[language],
      icon: Wallet,
      color: '#FFB81C',
      logo: 'https://customer-assets.emergentagent.com/job_ekip-showcase/artifacts/xwwmtwz6_Logo%20Filiale%20Kipay.png'
    },
  ];

  const stats = t.stats.items;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background with image overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#003366]/95 via-[#003366]/90 to-[#003366]/95 z-10" />
          <img 
            src="https://customer-assets.emergentagent.com/job_ekip-showcase/artifacts/drf8clwf_Photo%20Wayne%20Energy.avif" 
            alt="Wayne Energy" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="inline-block mb-6">
              <span className="bg-[#FF6600] text-white px-6 py-2 rounded-full text-sm font-semibold">
                {t.hero.badge[language]}
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              {t.hero.title[language]}<br />
              <span className="text-[#FFB81C]">{t.hero.titleHighlight[language]}</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-12 leading-relaxed max-w-3xl">
              {t.hero.description[language]}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                asChild
                size="lg"
                className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white text-lg px-8 py-6 transition-all duration-200"
              >
                <Link to="/about">
                  {t.hero.btn1[language]}
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </Button>
              <Button 
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#003366] text-lg px-8 py-6 transition-all duration-200"
              >
                <Link to="/subsidiaries">{t.hero.btn2[language]}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              {t.vision.title[language]}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t.vision.description[language]}
            </p>
          </div>

          <Card className="border-2 border-[#00A651] shadow-xl">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-[#00A651] p-3 rounded-lg">
                  <Zap className="text-white" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-[#003366] pt-2">{t.vision.ambitionTitle[language]}</h3>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t.vision.ambitionText[language]}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#003366] mb-16">
            {t.stats.title[language]}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const iconMap = { 0: Building2, 1: MapPin, 2: TrendingUp, 3: Users };
              const Icon = iconMap[index];
              return (
                <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300 border-0 shadow-md">
                  <CardContent className="p-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF6600]/10 rounded-full mb-4">
                      <Icon className="text-[#FF6600]" size={32} />
                    </div>
                    <div className="text-4xl font-bold text-[#003366] mb-2">{stat.value}</div>
                    <div className="text-gray-600 font-medium">{stat.label[language]}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Subsidiaries Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              {t.subsidiaries.title[language]}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.subsidiaries.subtitle[language]}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {subsidiaries.map((sub, index) => {
              const Icon = sub.icon;
              return (
                <Card 
                  key={index} 
                  className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#FF6600] overflow-hidden"
                >
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex-1">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg mb-4" style={{ backgroundColor: `${sub.color}15` }}>
                          <Icon style={{ color: sub.color }} size={28} />
                        </div>
                        <h3 className="text-2xl font-bold text-[#003366] mb-2">{sub.name}</h3>
                        <p className="text-lg font-semibold mb-3" style={{ color: sub.color }}>{sub.tagline}</p>
                      </div>
                      <img src={sub.logo} alt={sub.name} className="h-20 w-auto ml-4" />
                    </div>
                    <p className="text-gray-600 mb-6 leading-relaxed">{sub.description}</p>
                    <Link 
                      to="/subsidiaries" 
                      className="inline-flex items-center text-[#FF6600] font-semibold group-hover:translate-x-2 transition-transform duration-200"
                    >
                      {t.subsidiaries.learnMore[language]}
                      <ArrowRight className="ml-2" size={18} />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#003366] to-[#004080]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.cta.title[language]}
          </h2>
          <p className="text-xl text-gray-200 mb-10">
            {t.cta.description[language]}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white text-lg px-10 py-6"
            >
              <Link to="/contact">
                {t.cta.btn1[language]}
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#003366] text-lg px-10 py-6"
            >
              <Link to="/synergies">{t.cta.btn2[language]}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};